import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/Client";
import "../../styles/auth.css";
import { SecureStorage } from "../../storage/SecureStorage";

const LockIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
    <path
      d="M17 8h-1V6a4 4 0 1 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-6 0V6a2 2 0 1 1 4 0v2h-4Z"
      fill="currentColor"
    />
  </svg>
);

const EyeIcon: React.FC<{ off?: boolean }> = ({ off }) =>
  off ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
      <path
        d="M12 5C6 5 2 12 2 12s4 7 10 7 10-7 10-7S18 5 12 5Zm0 10a3 3 0 0 1-3-3 3 3 0 0 1 4.6-2.7l4-4A9.5 9.5 0 0 0 12 7c-5 0-9 5-9 5s4 5 9 5a9.5 9.5 0 0 0 4.3-1l-3.4-3.4A3 3 0 0 1 12 15Z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
      <path
        d="M12 5c6 0 10 7 10 7s-4 7-10 7S2 12 2 12s4-7 10-7Zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
        fill="currentColor"
      />
    </svg>
  );

// Move PasswordField outside of ChangePasswordPage to prevent remounting and focus loss
type PasswordFieldProps = {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  visible: boolean;
  toggleVisible: () => void;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  placeholder,
  value,
  onChange,
  visible,
  toggleVisible,
}) => (
  <div className="input-wrap">
    <LockIcon />
    <input
      className="auth-input"
      type={visible ? "text" : "password"}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
    />
    <button
      type="button"
      className="toggle-eye"
      onClick={(e) => {
        e.preventDefault(); // evita submit accidental
        e.stopPropagation(); // evita que robe el foco
        toggleVisible();
      }}
      tabIndex={-1} // evita que entre en el flujo de tabulación
    >
      <EyeIcon off={!visible} />
    </button>
  </div>
);

export default function ChangePasswordPage() {
  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleChangePassword = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const user = await SecureStorage.getUser();
    if (!user?.id) {
      setMessage("❌ Usuario no válido, vuelve a iniciar sesión");
      return;
    }

    if (newPass !== confirm) {
      setMessage("❌ Las contraseñas no coinciden");
      return;
    } 

    try {
      setLoading(true);
      console.log("📤 Enviando:", {
        currentPassword: current,
        newPassword: newPass,
        userId: user.id,
      });

      const res = await api.put("/api/mobile/change-password", {
        currentPassword: current,
        newPassword: newPass,
        userId: Number(user.id),
      });

      const data = res.data as { message?: string };
      setMessage(data.message || "✅ Contraseña cambiada correctamente");
      navigate(-1);
    } catch (err: any) {
      console.error("❌ Error cambiando contraseña:", err.response?.data || err.message);
      setMessage(err.response?.data?.error || "❌ No estás autorizado");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-page">
        <div className="auth-container">
          <h2 className="auth-title">Cambiar contraseña</h2>
          <form onSubmit={handleChangePassword} noValidate>
            <PasswordField
              placeholder="Contraseña actual"
              value={current}
              onChange={setCurrent}
              visible={showCurrent}
              toggleVisible={() => setShowCurrent((s) => !s)}
            />
            <PasswordField
              placeholder="Nueva contraseña"
              value={newPass}
              onChange={setNewPass}
              visible={showNew}
              toggleVisible={() => setShowNew((s) => !s)}
            />
            <PasswordField
              placeholder="Confirmar nueva contraseña"
              value={confirm}
              onChange={setConfirm}
              visible={showConfirm}
              toggleVisible={() => setShowConfirm((s) => !s)}
            />

            {message && (
              <p
                className="auth-error"
                style={{ color: message.startsWith("✅") ? "green" : "red" }}
              >
                {message}
              </p>
            )}

            <button type="submit" className="auth-button" disabled={loading} style={{ color: "#fff" }}>
              {loading ? "Cambiando..." : "Guardar cambios"}
            </button>
          </form>
        </div>
      </div>
      <div className="bottom-bar" />
    </>
  );
}
